/**
 * Token & Cost Tracking
 * Approximate token counting and cost estimation per conversation and globally.
 */

import { db } from './db.js';

const USAGE_KEY = 'usage_data';

/**
 * Approximate pricing per 1M tokens (input/output combined estimate)
 * These are rough averages — actual costs vary by provider.
 */
const PRICING = {
  'claude-opus-4-6-thinking':   { input: 15.0, output: 75.0 },
  'claude-sonnet-4-5-thinking': { input: 3.0,  output: 15.0 },
  'claude-sonnet-4-5':          { input: 3.0,  output: 15.0 },
  'gemini-3-pro':               { input: 1.25, output: 5.0 },
  'gemini-3-flash':             { input: 0.075, output: 0.30 },
  'gemini-2.5-pro':             { input: 1.25, output: 5.0 },
  'gemini-2.5-flash':           { input: 0.075, output: 0.30 },
  'deepseek-chat':              { input: 0.27, output: 1.10 },
  'deepseek-reasoner':          { input: 0.55, output: 2.19 },
  'default':                    { input: 1.0,  output: 3.0 },
};

let usageData = {
  conversations: {},  // { [convId]: { inputTokens, outputTokens, cost, messageCount } }
  total: { inputTokens: 0, outputTokens: 0, cost: 0, messageCount: 0 },
};
let loaded = false;

/**
 * Estimate token count from text (rough: ~1.3 tokens per word)
 * @param {string} text
 * @returns {number}
 */
export function estimateTokens(text) {
  if (!text) return 0;
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words * 1.3);
}

/**
 * Estimate cost for a given model and token counts
 * @param {string} model - Model ID
 * @param {number} inputTokens
 * @param {number} outputTokens
 * @returns {number} Cost in USD
 */
export function estimateCost(model, inputTokens, outputTokens) {
  const pricing = PRICING[model] || PRICING.default;
  return (inputTokens * pricing.input + outputTokens * pricing.output) / 1_000_000;
}

/**
 * Load usage data from IndexedDB
 */
export async function loadUsage() {
  if (loaded) return usageData;
  try {
    const stored = await db.loadSetting(USAGE_KEY);
    if (stored) usageData = stored;
    loaded = true;
  } catch {
    // Keep defaults
  }
  return usageData;
}

/**
 * Get current usage data (sync)
 */
export function getUsage() {
  return usageData;
}

/**
 * Track token usage for a message exchange
 * @param {string} conversationId
 * @param {string} model - Model ID used
 * @param {string} inputText - User message + context
 * @param {string} outputText - Assistant response
 */
export async function trackUsage(conversationId, model, inputText, outputText) {
  const inputTokens = estimateTokens(inputText);
  const outputTokens = estimateTokens(outputText);
  const cost = estimateCost(model, inputTokens, outputTokens);

  // Update conversation usage
  if (!usageData.conversations[conversationId]) {
    usageData.conversations[conversationId] = {
      inputTokens: 0, outputTokens: 0, cost: 0, messageCount: 0,
    };
  }
  const conv = usageData.conversations[conversationId];
  conv.inputTokens += inputTokens;
  conv.outputTokens += outputTokens;
  conv.cost += cost;
  conv.messageCount += 1;

  // Update totals
  usageData.total.inputTokens += inputTokens;
  usageData.total.outputTokens += outputTokens;
  usageData.total.cost += cost;
  usageData.total.messageCount += 1;

  // Persist (debounced — we'll save on every call since it's fast)
  await db.saveSetting(USAGE_KEY, usageData);

  return { inputTokens, outputTokens, cost };
}

/**
 * Get usage for a specific conversation
 * @param {string} conversationId
 * @returns {{ inputTokens: number, outputTokens: number, cost: number, messageCount: number }}
 */
export function getConversationUsage(conversationId) {
  return usageData.conversations[conversationId] || {
    inputTokens: 0, outputTokens: 0, cost: 0, messageCount: 0,
  };
}

/**
 * Format token count for display (e.g., "1.2K", "3.5M")
 * @param {number} tokens
 * @returns {string}
 */
export function formatTokens(tokens) {
  if (tokens < 1000) return String(tokens);
  if (tokens < 1_000_000) return (tokens / 1000).toFixed(1) + 'K';
  return (tokens / 1_000_000).toFixed(2) + 'M';
}

/**
 * Format cost for display
 * @param {number} cost - In USD
 * @returns {string}
 */
export function formatCost(cost) {
  if (cost < 0.01) return '<$0.01';
  if (cost < 1) return '$' + cost.toFixed(3);
  return '$' + cost.toFixed(2);
}

/**
 * Reset all usage data
 */
export async function resetUsage() {
  usageData = {
    conversations: {},
    total: { inputTokens: 0, outputTokens: 0, cost: 0, messageCount: 0 },
  };
  await db.saveSetting(USAGE_KEY, usageData);
}
